/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { DROPDOWN_SX, TEAL, TEAL_LIGHT, formatDisplayDate, formatFullDateTime, getCurrentUserId } from '../opdAppointments/constants';
import { printOpdBill, printOpdInvoice, printOpdReceipt } from './print';

const STATUS_CHIP_COLOR = {
  PENDING: 'warning',
  PAID: 'success',
  PARTIALLY_REFUNDED: 'info',
  REFUNDED: 'default',
  CANCELLED: 'error',
};

function feeLabel(line) {
  return line.fee_type === 'REGISTRATION' ? 'Registration Fee' : 'Consultation Fee';
}

export default function OpdBillDetail() {
  const navigate = useNavigate();
  const { billId } = useParams();
  const { hasPermission } = usePermissions();

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_mode: 'CASH', amount: '', reference_no: '' });
  const [collectingPayment, setCollectingPayment] = useState(false);

  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelForm, setCancelForm] = useState({ reason: '', refund_method: 'CASH' });
  const [cancelling, setCancelling] = useState(false);

  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundForm, setRefundForm] = useState({ refund_type: 'FULL', amount: '', refund_method: 'CASH', reason: '' });
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.opdBilling
      .getBillById(billId)
      .then(resp => setBill(resp?.data || null))
      .catch(() => setBill(null))
      .finally(() => setLoading(false));
  }, [billId]);

  useEffect(() => {
    load();
  }, [load]);

  const patientName = () => {
    const c = bill?.contactInfo;
    if (!c) return `Patient #${bill?.irm_contacts_id}`;
    return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ') || `Patient #${bill.irm_contacts_id}`;
  };

  const openPaymentForm = () => {
    setPaymentForm({
      payment_mode: 'CASH',
      amount: bill?.balance_amount !== undefined && bill?.balance_amount !== null ? String(bill.balance_amount) : '',
      reference_no: '',
    });
    setShowPaymentForm(true);
  };

  const handleCollectPayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      Swal.fire({ icon: 'warning', title: 'Enter a valid amount' });
      return;
    }
    setCollectingPayment(true);
    try {
      await api.opdBilling.collectPayment(bill.id, {
        payment_mode: paymentForm.payment_mode,
        amount,
        reference_no: paymentForm.reference_no || undefined,
        created_by: getCurrentUserId(),
      });
      setShowPaymentForm(false);
      Swal.fire({ icon: 'success', title: 'Payment collected', timer: 1500, showConfirmButton: false });
      load();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setCollectingPayment(false);
    }
  };

  const refundedTotal = (bill?.refunds || []).reduce((sum, r) => sum + Number(r.amount), 0);
  const refundableBalance = bill ? Math.max(Number(bill.paid_amount) - refundedTotal, 0) : 0;

  const openCancelForm = () => {
    setCancelForm({ reason: '', refund_method: 'CASH' });
    setShowCancelForm(true);
  };

  const handleCancelAndRegenerate = async () => {
    if (!cancelForm.reason.trim()) {
      Swal.fire({ icon: 'warning', title: 'Enter a cancellation reason' });
      return;
    }
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Cancel this bill?',
      text: 'A fresh bill will be generated for this appointment.',
      showCancelButton: true,
      confirmButtonText: 'Cancel & Regenerate',
    });
    if (!confirm.isConfirmed) return;

    setCancelling(true);
    try {
      const resp = await api.opdBilling.cancelAndRegenerate(bill.id, {
        reason: cancelForm.reason,
        refund_method: Number(bill.paid_amount) > 0 ? cancelForm.refund_method : undefined,
        cancelled_by: getCurrentUserId(),
      });
      setShowCancelForm(false);
      Swal.fire({ icon: 'success', title: 'Bill cancelled, new bill generated', timer: 1800, showConfirmButton: false });
      const newBillId = resp?.data?.new_bill?.id;
      if (newBillId) {
        navigate(`/opd/billing/${newBillId}`, { replace: true });
      } else {
        load();
      }
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setCancelling(false);
    }
  };

  const openRefundForm = () => {
    const canPartial = hasPermission('v1-opd-billing-refund-partial');
    setRefundForm({
      refund_type: canPartial ? 'PARTIAL' : 'FULL',
      amount: canPartial ? '' : String(refundableBalance),
      refund_method: 'CASH',
      reason: '',
    });
    setShowRefundForm(true);
  };

  const handleRefund = async () => {
    const payload = {
      refund_method: refundForm.refund_method,
      reason: refundForm.reason || undefined,
      refunded_by: getCurrentUserId(),
    };

    setRefunding(true);
    try {
      if (refundForm.refund_type === 'PARTIAL') {
        const amount = parseFloat(refundForm.amount);
        if (!amount || amount <= 0) {
          Swal.fire({ icon: 'warning', title: 'Enter a valid refund amount' });
          setRefunding(false);
          return;
        }
        if (amount > refundableBalance) {
          Swal.fire({ icon: 'warning', title: `Amount exceeds refundable balance of ₹${refundableBalance.toFixed(2)}` });
          setRefunding(false);
          return;
        }
        await api.opdBilling.refundPartial(bill.id, { ...payload, amount });
      } else {
        await api.opdBilling.refundFull(bill.id, payload);
      }
      setShowRefundForm(false);
      Swal.fire({ icon: 'success', title: 'Refund processed', timer: 1500, showConfirmButton: false });
      load();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setRefunding(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/opd/billing')} sx={{ textTransform: 'none', color: TEAL }}>
            Back to OPD Billing
          </Button>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: TEAL }} />
          </Box>
        )}

        {!loading && !bill && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">Bill not found.</Typography>
          </Card>
        )}

        {!loading && bill && (
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2.5, background: `linear-gradient(135deg, ${TEAL} 0%, #005f70 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: 700, opacity: 0.85 }}>
                  {bill.bill_no || `#${bill.id}`}
                </Typography>
                <MDTypography variant="h5" fontWeight="bold" sx={{ color: '#fff !important' }}>
                  {patientName()}
                </MDTypography>
              </Box>
              <Chip label={bill.status} color={STATUS_CHIP_COLOR[bill.status] || 'default'} sx={{ fontWeight: 700 }} />
            </Box>

            <MDBox p={3}>
              <Box display="flex" flexWrap="wrap" gap={4} mb={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Doctor</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{bill.doctorInfo?.name || `Doctor #${bill.irm_users_id}`}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Bill Date</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{formatDisplayDate(bill.bill_date)}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Fee Lines
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                {(bill.lines || []).map(line => (
                  <Box key={line.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>
                      {feeLabel(line)}
                      {line.is_waived === 'Y' && (
                        <Typography component="span" sx={{ fontSize: '0.72rem', color: '#8d6e63', ml: 0.75 }}>
                          (waived — {line.waived_reason})
                        </Typography>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 600 }}>₹{Number(line.amount).toFixed(2)}</Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between"><Typography fontWeight={700}>Total</Typography><Typography fontWeight={700} fontFamily="monospace">₹{Number(bill.total_amount).toFixed(2)}</Typography></Box>
                <Box display="flex" justifyContent="space-between"><Typography color="success.main">Paid</Typography><Typography color="success.main" fontFamily="monospace">₹{Number(bill.paid_amount).toFixed(2)}</Typography></Box>
                {Number(bill.balance_amount) > 0 && (
                  <Box display="flex" justifyContent="space-between"><Typography color="error.main" fontWeight={700}>Balance</Typography><Typography color="error.main" fontWeight={700} fontFamily="monospace">₹{Number(bill.balance_amount).toFixed(2)}</Typography></Box>
                )}
                {refundedTotal > 0 && (
                  <Box display="flex" justifyContent="space-between"><Typography color="warning.main">Refunded</Typography><Typography color="warning.main" fontFamily="monospace">₹{refundedTotal.toFixed(2)}</Typography></Box>
                )}
              </Box>

              {bill.status === 'CANCELLED' && (
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fdecea', border: '1px solid #f5c6cb', mb: 2 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#c62828', fontWeight: 600 }}>
                    Cancelled{bill.cancelled_at ? ` on ${formatFullDateTime(bill.cancelled_at)}` : ''}
                  </Typography>
                  {bill.cancel_reason && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#c62828' }}>Reason: {bill.cancel_reason}</Typography>
                  )}
                </Box>
              )}

              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                {bill.status !== 'PAID' && bill.status !== 'CANCELLED' && !showPaymentForm && (
                  <MDButton variant="gradient" color="logo" size="small" startIcon={<CurrencyRupeeIcon />} onClick={openPaymentForm}>
                    Collect Payment
                  </MDButton>
                )}
                {bill.status !== 'CANCELLED' && hasPermission('v1-opd-billing-cancel-regenerate') && !showCancelForm && (
                  <MDButton variant="outlined" color="error" size="small" startIcon={<CancelIcon />} onClick={openCancelForm}>
                    Cancel &amp; Regenerate
                  </MDButton>
                )}
                {bill.status !== 'CANCELLED' && refundableBalance > 0 && hasPermission('v1-opd-billing-refund-full') && !showRefundForm && (
                  <MDButton variant="outlined" color="warning" size="small" startIcon={<UndoIcon />} onClick={openRefundForm}>
                    Refund
                  </MDButton>
                )}
                <MDButton variant="outlined" color="info" size="small" startIcon={<DescriptionIcon />} onClick={() => printOpdBill(bill)}>
                  Print Bill
                </MDButton>
                <MDButton variant="outlined" color="info" size="small" startIcon={<PrintIcon />} onClick={() => printOpdInvoice(bill)}>
                  Print Invoice
                </MDButton>
                {(bill.payments || []).some(p => p.status === 'PAID') && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<ReceiptIcon />}
                    onClick={() => {
                      const paid = (bill.payments || []).filter(p => p.status === 'PAID');
                      printOpdReceipt(bill, paid[paid.length - 1]);
                    }}
                  >
                    Print Receipt
                  </MDButton>
                )}
              </Box>

              {showPaymentForm && (
                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #eef0f2', bgcolor: '#fafbfc', maxWidth: 360, mb: 2 }}>
                  <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <InputLabel id="bd-payment-mode-label">Payment Mode</InputLabel>
                    <Select
                      labelId="bd-payment-mode-label"
                      label="Payment Mode"
                      value={paymentForm.payment_mode}
                      onChange={e => setPaymentForm(f => ({ ...f, payment_mode: e.target.value }))}
                      sx={DROPDOWN_SX}
                    >
                      <MenuItem value="CASH">Cash</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="CARD">Card</MenuItem>
                      <MenuItem value="ONLINE">Online</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Amount"
                    size="small"
                    type="number"
                    fullWidth
                    sx={{ mb: 1.5 }}
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  />
                  {paymentForm.payment_mode !== 'CASH' && (
                    <TextField
                      label="Reference No (optional)"
                      size="small"
                      fullWidth
                      sx={{ mb: 1.5 }}
                      value={paymentForm.reference_no}
                      onChange={e => setPaymentForm(f => ({ ...f, reference_no: e.target.value }))}
                    />
                  )}
                  <Box display="flex" gap={1}>
                    <Button size="small" onClick={() => setShowPaymentForm(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <MDButton size="small" variant="gradient" color="logo" disabled={collectingPayment} onClick={handleCollectPayment}>
                      {collectingPayment ? 'Collecting…' : 'Confirm Payment'}
                    </MDButton>
                  </Box>
                </Box>
              )}

              {showCancelForm && (
                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #f5c6cb', bgcolor: '#fff8f8', maxWidth: 400, mb: 2 }}>
                  <TextField
                    label="Cancellation Reason"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mb: 1.5 }}
                    value={cancelForm.reason}
                    onChange={e => setCancelForm(f => ({ ...f, reason: e.target.value }))}
                  />
                  {Number(bill.paid_amount) > 0 && (
                    <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                      <InputLabel id="bd-cancel-refund-method-label">Refund Method</InputLabel>
                      <Select
                        labelId="bd-cancel-refund-method-label"
                        label="Refund Method"
                        value={cancelForm.refund_method}
                        onChange={e => setCancelForm(f => ({ ...f, refund_method: e.target.value }))}
                        sx={DROPDOWN_SX}
                      >
                        <MenuItem value="CASH">Cash</MenuItem>
                        <MenuItem value="ONLINE">Online</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <Box display="flex" gap={1}>
                    <Button size="small" onClick={() => setShowCancelForm(false)} sx={{ textTransform: 'none' }}>Close</Button>
                    <MDButton size="small" variant="gradient" color="error" disabled={cancelling} onClick={handleCancelAndRegenerate}>
                      {cancelling ? 'Cancelling…' : 'Confirm Cancel & Regenerate'}
                    </MDButton>
                  </Box>
                </Box>
              )}

              {showRefundForm && (
                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #ffe8a1', bgcolor: '#fffdf5', maxWidth: 400, mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    Refundable balance: ₹{refundableBalance.toFixed(2)}
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <InputLabel id="bd-refund-type-label">Refund Type</InputLabel>
                    <Select
                      labelId="bd-refund-type-label"
                      label="Refund Type"
                      value={refundForm.refund_type}
                      onChange={e => {
                        const refund_type = e.target.value;
                        setRefundForm(f => ({ ...f, refund_type, amount: refund_type === 'FULL' ? String(refundableBalance) : '' }));
                      }}
                      sx={DROPDOWN_SX}
                    >
                      <MenuItem value="FULL">Full</MenuItem>
                      {hasPermission('v1-opd-billing-refund-partial') && <MenuItem value="PARTIAL">Partial</MenuItem>}
                    </Select>
                  </FormControl>
                  {refundForm.refund_type === 'PARTIAL' && (
                    <TextField
                      label="Refund Amount"
                      size="small"
                      type="number"
                      fullWidth
                      sx={{ mb: 1.5 }}
                      value={refundForm.amount}
                      onChange={e => setRefundForm(f => ({ ...f, amount: e.target.value }))}
                    />
                  )}
                  <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                    <InputLabel id="bd-refund-method-label">Refund Method</InputLabel>
                    <Select
                      labelId="bd-refund-method-label"
                      label="Refund Method"
                      value={refundForm.refund_method}
                      onChange={e => setRefundForm(f => ({ ...f, refund_method: e.target.value }))}
                      sx={DROPDOWN_SX}
                    >
                      <MenuItem value="CASH">Cash</MenuItem>
                      <MenuItem value="ONLINE">Online</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Reason (optional)"
                    size="small"
                    fullWidth
                    sx={{ mb: 1.5 }}
                    value={refundForm.reason}
                    onChange={e => setRefundForm(f => ({ ...f, reason: e.target.value }))}
                  />
                  <Box display="flex" gap={1}>
                    <Button size="small" onClick={() => setShowRefundForm(false)} sx={{ textTransform: 'none' }}>Close</Button>
                    <MDButton size="small" variant="gradient" color="warning" disabled={refunding} onClick={handleRefund}>
                      {refunding ? 'Processing…' : 'Confirm Refund'}
                    </MDButton>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Accordion
                disableGutters
                elevation={0}
                defaultExpanded={(bill.payments || []).length > 0}
                sx={{ '&:before': { display: 'none' }, border: '1px solid #eef0f2', borderRadius: '10px !important', overflow: 'hidden' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 0.75 } }}
                >
                  <HistoryIcon sx={{ fontSize: 17, color: TEAL }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2d3748' }}>Payment History</Typography>
                  <Chip label={(bill.payments || []).length} size="small" sx={{ height: 18, fontSize: '0.62rem', ml: 0.25, bgcolor: `${TEAL}1a`, color: TEAL, fontWeight: 700 }} />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {(bill.payments || []).length === 0 && (
                    <Typography variant="body2" color="textSecondary">No payments yet.</Typography>
                  )}
                  {(bill.payments || []).map(p => (
                    <Box key={p.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.75, borderBottom: '1px solid #f5f5f5' }}>
                      <Box>
                        <Chip label={p.payment_mode} size="small" sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />
                        <Typography component="span" variant="body2" color="textSecondary">{formatFullDateTime(p.paid_at)}</Typography>
                        {p.reference_no && <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>Ref: {p.reference_no}</Typography>}
                      </Box>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: p.status === 'CANCELLED' ? '#bdbdbd' : '#2e7d32', textDecoration: p.status === 'CANCELLED' ? 'line-through' : 'none' }}>
                        ₹{Number(p.amount).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>

              {(bill.refunds || []).length > 0 && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{ mt: 1.5, '&:before': { display: 'none' }, border: '1px solid #ffe0b2', borderRadius: '10px !important', overflow: 'hidden' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 42, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 0.75 } }}
                  >
                    <ReplayIcon sx={{ fontSize: 17, color: '#e65100' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2d3748' }}>Refund History</Typography>
                    <Chip label={bill.refunds.length} size="small" sx={{ height: 18, fontSize: '0.62rem', ml: 0.25, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {bill.refunds.map(r => (
                      <Box key={r.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.75, borderBottom: '1px solid #f5f5f5' }}>
                        <Box>
                          <Chip label={r.refund_type} size="small" color={r.refund_type === 'FULL' ? 'error' : 'warning'} sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />
                          <Chip label={r.refund_method} size="small" sx={{ mr: 1, height: 20, fontSize: '0.65rem' }} />
                          <Typography component="span" variant="body2" color="textSecondary">{formatFullDateTime(r.refunded_at)}</Typography>
                          {r.reason && <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>— {r.reason}</Typography>}
                        </Box>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#c62828' }}>
                          -₹{Number(r.amount).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </MDBox>
          </Card>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
